import { Button, Input, Modal, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS, TYPE_PAGE } from '../../constants';
import { selectAllUsers } from '../../modules/users/store/slice';
import { doCreateUser, doGetUsers, doUpdateUser } from '../../modules/users/store/thunk';
import UsersFilter from '../Forms/Filters/UsersFilter';
import UserForm from '../Forms/Users/UserForm';
import UsersTable from '../Tables/UsersTable';
import withPage from './withPage';
import usePageView from '../../hooks/usePageView';

const UsersPage = ({ query, pushTo }) => {
  const dispatch = useDispatch();

  const isFetching = useSelector((state) => state.user.isFetching);
  const action = useSelector((state) => state.user.action);
  const status = useSelector((state) => state.user.status);
  const metadata = useSelector((state) => state.user.metadata);
  const users = useSelector((state) => selectAllUsers(state));
  const [userDetail, setUserDetail] = useState(false);
  const [typeUser, setTypeUser] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Emit page view event
  usePageView(window.location.href);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  const currentPage = Number(query.current) || 1;
  const itemsPerPage = Number(query.pageSize) || PAGE_SIZE;

  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    locale: { items_per_page: 'Users/Page' },
    total: metadata?.totalItems || 0,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showSizeChanger: true,
    showTotal: (total) => `${total} Users`,
  };

  useEffect(() => {
    dispatch(doGetUsers({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  useEffect(() => {
    if (['create', 'update'].includes(action) && status === 'succeeded') {
      dispatch(doGetUsers({ page: currentPage, take: itemsPerPage, ...query }));
      cancelModal();
    }
  }, [isFetching]);

  const onClickEditUser = (id) => {
    setTypeUser(TYPE_PAGE.EDIT);
    const user = users.find((l) => l.id === id);
    setUserDetail({ ...user });
    setIsModalOpen(true);
  };

  const handleActionUser = (data) => {
    const params = {
      ...data,
    };
    if (userDetail && typeUser === TYPE_PAGE.EDIT) {
      params.id = userDetail.id;
      dispatch(doUpdateUser(params));
    } else {
      dispatch(doCreateUser(params));
    }
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetUsers({
        page: newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const cancelModal = () => {
    if (userDetail) setUserDetail(undefined);
    setIsModalOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const onClickNewUser = () => {
    setTypeUser(TYPE_PAGE.NEW);
    setIsModalOpen(true);
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Button type="link" className="edit">
          <ReactSVG className="icon" src="/icons/edit.svg" onClick={() => onClickEditUser(record.id)} />
        </Button>
        {/* <Button type="link" className="more">
          <ReactSVG className="icon" src="/icons/more.svg" />
        </Button> */}
      </div>
    );
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  return (
    <Spin spinning={isFetching}>
      <div className="Content UsersPage">
        <div className="ContentHeader flex items-center justify-between mb-4">
          <div className="HeaderTitle text-xl font-semibold">Users</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons flex items-center gap-x-2">
              <Button className="CreateButton ButtonPrimary" type="primary" size="medium" onClick={onClickNewUser}>
                Create New User
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </div>
          </div>
        </div>
        <div className="users-header-table page-header-table flex items-center justify-between mb-2">
          <div className="search flex-1">
            <Input
              onChange={handleChangeSearch}
              onBlur={handleSearch}
              placeholder="Search"
              onKeyDown={handleKeyDown}
              suffix={<img src="/icons/search.svg" />}
              disabled={isFetching}
              value={searchValue}
            />
          </div>
          <div className="buttons flex items-center gap-x-2">
            {selectedRowKeys.length > 0 && (
              <Button className="FilterButton ButtonFilter">
                {selectedRowKeys?.length > 0 && <div className="count">{selectedRowKeys.length}</div>}
                Action <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
              </Button>
            )}

            <Button className="FilterButton ButtonFilter" onClick={() => setIsFilterOpen(!isFilterOpen)}>
              Filter
              <ReactSVG className="icon" src="/icons/filter_list.svg" />
            </Button>
          </div>
        </div>
        {isFilterOpen && <UsersFilter isFetching={isFetching} query={query} onSubmit={handleFilter} />}
        <div className="ContentMain">
          <UsersTable
            onChange={handleTableChange}
            dataSource={users}
            pagination={pagination}
            searchValue={searchValue}
            withAction={withAction}
            onSelectChange={onSelectChange}
            selectedRowKeys={selectedRowKeys}
            onSearch={handleSearch}
            onChangeSearch={handleChangeSearch}
            rowKey="id"
            isFetching={isFetching}
          />
        </div>

        <Modal
          width={552}
          closeIcon={false}
          title={typeUser === TYPE_PAGE.NEW ? 'Create New User' : 'Edit User'}
          open={isModalOpen}
          forceRender
        >
          <UserForm
            handleCancel={cancelModal}
            handleOk={(values) => handleActionUser(values)}
            isModalOpen={isModalOpen}
            user={userDetail}
            typePage={typeUser}
            isFetching={isFetching}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default withPage(UsersPage);
